export interface ProductInterface {
  category?: { name: string };
  categoryId: string;
  descriptions: ProductFormText;
  id: string;
  names: ProductFormText;
  photo: string | Media;
  productPhoto: {
    id: string;
    publicId: string;
    secureUrl: string;
    mediaSource?: MediaSource;
  };
  mediaSource: number;
  prices: ProductFormPrice;
  inStock: boolean;
  editMode?: boolean;
  sales?: [
    | {
        bartender: string;
        dayStartStock: number;
        dayEndStock: number;
        sales: number;
      }
    | any
  ];
}
export interface Category {
  createdAt: string;
  id: string;
  drinks?: ProductInterface[];
  food?: ProductInterface[];
  name: string;
  updatedAt: string;
  userId: string;
}
export interface Categories {
  categories: Array<Category>;
  productType: ProductTypes;
}
export type ProductTypes = "Drink" | "Food"; //todo: replace all usage with ProductType enum in product.tsx
export interface ModalSubComponent {
  activeProduct: ProductInterface;
  modalMode: number;
  productType: number;
}

export type XHR_Error = {
  error: { message: string };
  originalData: Record<string, any>;
};
export interface ProductResponse {
  success: boolean;
  addedRecord?: Record<string, any>;
  products?: ProductInterface[];
  product?: any;
  error?: any;
}

export const FEEDBACK_ALERTS = {
  PRODUCT_ADDED: "Product Added",
  PRODUCT_UPDATED: "Product Updated",
  PRODUCT_DELETED: "Product Deleted",
  CREATE_PRODUCT: {
    STEP_REQUIRED: "Please complete the current step",
  },
  LARGE_FILE_SIZE: "Please upload a photo not exceeding a 500kb file size.",
  INVALID_FILE: "Please upload a jpg, jpeg, png or svg file.",
  UNKNOWN_ERR: "The process could not be completed.",
};

export interface ProductFormData {
  name: string;
  description: string;
  categoryId: string; // categoryID
  price: number;
  photo: string;
  stockCount?: number;
}

export interface BusinessInfo {
  address?: string;
  brief?: string;
  contacts?: string[];
  activities?: string[];
  businessProfile?: BusinessProfile;
}

export type PaymentProviderAccount = {
  provider: string;
  providerAccountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  onboardingStatus: "pending" | "active" | "restricted" | "disconnected" | string;
  defaultCurrency?: string | null;
  country?: string | null;
  capabilities?: unknown;
  metadata?: unknown;
  disconnectedAt?: string | null;
  updatedAt?: string | null;
};

export type OpeningTime = {
  isOpen: boolean;
  open?: string | null;
  close?: string | null;
};

export type BusinessProfilePhoto = {
  photoUrl: string;
  photoPublicId?: string | null;
};

export interface BusinessProfile {
  restaurantType?: string | null;
  amenities?: string[];
  isPetFriendly?: boolean;
  isKidFriendly?: boolean;
  openingTimes?: Record<string, OpeningTime>;
  parking?: string | null;
  seatingOptions?: string[];
  restaurantPhotos?: BusinessProfilePhoto[];
}

export interface Currency {
  prefix: string;
  name: string;
}

export interface Language {
  iso: string;
  name: string;
}

export interface Translation {
  iso: string;
  name: string;
  translations: { [key: string]: string };
}

export interface Segment {
  id: string;
  name: string;
  description?: string;
  priceMultiplier?: number;
  isDefault?: boolean;
  photo?: {
    photoUrl?: string;
    photoPublicId?: string;
  };
}

export interface RestaurantTable {
  id: string;
  userId: string;
  segmentId: string;
  label: string;
  seats?: number | null;
  status: number;
  sortOrder?: number | null;
  segment?: Segment;
}

export interface TableSessionOrder {
  id?: string;
  orderTotal: number;
  orderNumber: number;
  order: Array<{
    productId: string;
    price?: number;
    names: Record<string, string>;
    count: number;
  }>;
  tableNumber?: string;
  tableId?: string | null;
  status?: number;
  createdBy?: "customer" | "staff";
  orderName?: string | null;
  segmentName?: string | null;
  createdAt?: string | null;
}

export interface RestaurantTableSession {
  sessionId: string;
  tableNumber: string;
  tableId?: string | null;
  segmentName: string;
  openedAt: string;
  status: number;
  paymentStatus: number;
  amountPaid: number;
  subtotal: number;
  balanceDue: number;
  orders: TableSessionOrder[];
}

export interface User {
  currency: {
    prefix: string;
    name: string;
  };
  defaultLanguage: string;
  legalName: string;
  canPlaceOrder: boolean;
  orderPaymentPolicy?: "pay_after_session";
  paymentExperience?: "hosted_checkout" | "embedded_element";
  languages: Language[];
  segments: Segment[];
  hasSegments: boolean;
  username: string;
  userLanguages: Translation[];
  activeLanguage: string;
  membershipDetails: MembershipDetails;
  membershipPlan?: MembershipPlanSummary | null;
  contractType?: "plan" | "custom";
  features?: string[];
  address?: string;
  brief?: string;
  contacts?: string[];
  activities?: string[];
  businessProfile?: BusinessProfile;
  banner: string;
  logo?: string;
  isVerified: boolean;
  passwordResetRequired: boolean;
}
export interface ProductFormText {
  [key: string]: string;
}

export interface ProductFormPrice {
  [key: string]: number | undefined;
}

export interface NewProduct {
  descriptions: ProductFormText;
  inStock: boolean;
  names: ProductFormText;
  prices?: ProductFormPrice;
  categoryId: string;
  photo: string | null; // New photo. Could be null in case of product update when photo has not changed
  id: string;
  mediaSource: number;
  productPhoto?: {
    id: string;
    publicId: string;
    secureUrl: string;
    mediaSource?: MediaSource;
  };
}

export interface CategoryUpdateData {
  id: string;
  name: string;
  itemType: ProductTypes;
}

export enum StaffRole {
  ATTENDANT = 0,
  MANAGER = 2,
  KITCHEN = 3,
}

export type PermissionDefinition = {
  key: string;
  label: string;
  description?: string | null;
  category: string;
  isActive: boolean;
};

export type StaffRoleTemplate = {
  key: string;
  label: string;
  description?: string | null;
  role: StaffRole;
  permissions: string[];
  isEnabled: boolean;
};

export interface Staff {
  name: string;
  staffId: number;
  role: number;
  permissions: string[];
}

export interface StaffFormData {
  name: string;
  email: string;
  password: string;
  role: number;
  permissions: string[];
  welcomeMessage?: string;
}

export interface PasswordUpdateData {
  oldPassword: string;
  newPassword: string;
}

//=============
export enum ProductAddSteps {
  SelectProduct,
  SelectCategory,
  FillForm,
  Feedback,
}

export enum MediaSource {
  Upload = 1,
  MediaPicker,
}

export interface Media {
  id: string;
  photo: string;
  tags: string[];
}

export interface MembershipLimits {
  staff: number;
  product: number;
  segment: number;
  order: boolean;
}

export interface MembershipDetails {
  id: string;
  name: string;
  key?: string;
  price: number;
  description: string[];
  limits: MembershipLimits;
}

export interface MembershipPlanSummary {
  id: string;
  key: string;
  name: string;
}

export interface Feedback {
  id: string;
  rating: number;
  message: string;
  createdAt: string;
  guestName?: string;
  comment?: string;
  date?: string;
  tableNumber?: string;
  segmentName?: string;
  orderId?: string;
  tags?: string[];
}

export interface NewComboPayload {
  names: ProductFormText;
  prices: ProductFormPrice;
  descriptions: ProductFormText;
  photo: string;
  productIds: string[];
}

export interface UpdateComboPayload {
  names?: ProductFormText;
  prices?: ProductFormPrice;
  descriptions?: ProductFormText;
  productIds?: string[];
  disabled?: boolean;
}

export interface Combo {
  id: string;
  names: ProductFormText;
  prices: ProductFormPrice;
  descriptions: ProductFormText;
  comboPhoto: {
    id: string;
    publicId: string;
    secureUrl: string;
    comboId?: string;
  };
  productIds: string[];
  disabled: boolean;
  createdAt?: string;
}

export type PromotionDiscountType = "percentage" | "fixed_amount";
export type PromotionTargetType = "product" | "category" | "product_type";
export type PromotionProductType = "food" | "drink" | "combo";

export interface DiscountPromotion {
  id: string;
  name: string;
  description?: string | null;
  discountType: PromotionDiscountType;
  discountValue: number;
  targetType: PromotionTargetType;
  targetIds: string[];
  productType?: PromotionProductType | null;
  startsAt?: string | null;
  expiresAt?: string | null;
  isActive: boolean;
  createdAt?: string;
}

export type DiscountPromotionPayload = Omit<
  DiscountPromotion,
  "id" | "createdAt"
>;

export type RewardType = "PRODUCT" | "DISCOUNT";

export interface ProductReward {
  rewardType: "PRODUCT";
  productId: string;
}

export interface DiscountReward {
  rewardType: "DISCOUNT";
  discount: {
    name: string;
    value: number;
    expiryDurationDays: number;
  };
}

export type PackageReward = ProductReward | DiscountReward;

export interface NewPackage {
  packageName: string;
  riskLevel: "LOW" | "MEDIUM" | "HIGH";
  packageItems: PackageReward[];
}

export interface UpdatePackagePayload {
  packageName: string;
  packageItems: Array<ProductReward | DiscountReward>;
}

export interface DiscountDetails {
  name: string;
  value: number;
  expiryDurationDays: number;
}

export interface PackageItem {
  id: string;
  spinPackageId: string;
  rewardType: "PRODUCT" | "DISCOUNT";
  productId?: string;
  discount?: DiscountDetails;
  createdAt: string;
  updatedAt: string;
}

export interface Package {
  id: string;
  userId: string;
  riskLevel: "LOW" | "MEDIUM" | "HIGH";
  packageName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  packageItems: PackageItem[];
}

export interface CampaignSettings {
  maxDailyWins: number;
}

export type MenuLayoutPreset = "default" | "standard";

export interface MenuThemeColors {
  primary: string;
  secondary: string;
  background: string;
  surface: string;
  text: string;
  mutedText: string;
  accent: string;
}

export interface MenuThemeTypography {
  headingFont: string;
  bodyFont: string;
  fontScale: number;
}

export interface MenuThemeBranding {
  logoPlacement: "top-left" | "top-center" | "hidden";
  bannerPlacement: "top" | "behind-header" | "hidden";
  bannerStyle: "cover" | "contained" | "minimal";
}

export interface MenuThemeLayout {
  cardStyle: "elevated" | "outlined" | "minimal";
  menuDensity: "comfortable" | "compact";
  showCategoryImages: boolean;
  showProductDescriptions: boolean;
}

export interface MenuThemeConfig {
  id?: string;
  userId?: string;
  layoutPreset: MenuLayoutPreset;
  colors: MenuThemeColors;
  typography: MenuThemeTypography;
  branding: MenuThemeBranding;
  layout: MenuThemeLayout;
  createdAt?: string;
  updatedAt?: string | null;
}

export type UpdateMenuThemeConfig = Partial<{
  layoutPreset: MenuLayoutPreset;
  colors: Partial<MenuThemeColors>;
  typography: Partial<MenuThemeTypography>;
  branding: Partial<MenuThemeBranding>;
  layout: Partial<MenuThemeLayout>;
}>;
